import { useState } from "react";
import {
  Check,
  ChevronDown,
  Copy,
  Globe,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  ShieldCheck,
  Star,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { domains as seedDomains, type Domain } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { builder, type DomainItem } from "@/data/services";
import { ApiError } from "@/api/client";
import { SampleChip, SkeletonLine, StatusPill } from "./parts";

/** Domain plus the real backend id (sample rows have none). */
type DomainView = Domain & { id?: string };

/** Pull a human-readable message out of a failed mutation. */
function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | string | null;
    if (body && typeof body === "object" && body.message) return body.message;
  }
  return fallback;
}

/** Project-scoped custom domains (read client, no shared hook exists). */
function useDomains(projectId: string | undefined) {
  return useQuery({
    queryKey: ["domains", projectId],
    queryFn: () => builder.listDomains(projectId!),
    enabled: !!projectId,
    retry: false,
  });
}

const STATUS_LABEL: Record<Domain["status"], string> = {
  live: "Live",
  pending: "Pending DNS",
  error: "Needs attention",
};

function statusTone(s: Domain["status"]): "ok" | "pending" | "error" {
  return s === "live" ? "ok" : s === "pending" ? "pending" : "error";
}

/** Map a backend status string onto our 3-state pill vocabulary. */
function normalizeStatus(s: string): Domain["status"] {
  const v = s.toLowerCase();
  if (v === "live" || v === "verified" || v === "active") return "live";
  if (v === "error" || v === "failed" || v === "invalid") return "error";
  return "pending";
}

/** Real backend domain -> the shape DomainRow renders (id threaded through).
 *  `records` is keyed (a/cname/txt), each a full {type,host,value}. */
function mapBackendDomain(d: DomainItem): DomainView {
  const status = normalizeStatus(d.status);
  const order = ["a", "cname", "txt"];
  const dns: Domain["dns"] = Object.entries(d.records ?? {})
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([, rec]) => ({
      type: rec.type,
      name: rec.host,
      value: rec.value,
      ok: status === "live",
    }));
  if (d.verification_token && !dns.some((r) => r.type === "TXT")) {
    dns.push({ type: "TXT", name: "_landing-verify", value: `landing-verify=${d.verification_token}`, ok: status === "live" });
  }
  return {
    id: d.id,
    domain: d.hostname,
    status,
    ssl: status === "live" ? "active" : "provisioning",
    primary: false,
    dns,
  };
}

function SslBadge({ ssl }: { ssl: Domain["ssl"] }) {
  if (ssl === "active")
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[11px] text-muted">
        <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} /> SSL active
      </span>
    );
  if (ssl === "provisioning")
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[11px] text-muted">
        <Lock className="h-3.5 w-3.5" strokeWidth={1.75} /> SSL provisioning
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[11px] text-subtle">
      <Lock className="h-3.5 w-3.5" strokeWidth={1.75} /> no SSL
    </span>
  );
}

function DnsTable({ dns }: { dns: Domain["dns"] }) {
  const [copied, setCopied] = useState<string | null>(null);
  return (
    <div className="mt-3 overflow-hidden rounded-btn border border-hairline">
      <div className="grid grid-cols-[64px_72px_1fr_28px] items-center gap-2 border-b border-hairline bg-paper-2 px-3 py-2 dark:bg-white/5">
        <span className="eyebrow">Type</span>
        <span className="eyebrow">Name</span>
        <span className="eyebrow">Value</span>
        <span />
      </div>
      {dns.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[64px_72px_1fr_28px] items-center gap-2 border-b border-hairline/60 px-3 py-2.5 last:border-0"
        >
          <span className="font-mono text-[12px] text-ink-strong">{r.type}</span>
          <span className="font-mono text-[12px] text-muted">{r.name}</span>
          <span className="flex items-center gap-1.5 truncate font-mono text-[12px] text-muted">
            <span
              className={cn(
                "grid h-4 w-4 shrink-0 place-items-center rounded-full",
                r.ok ? "bg-emerald-500/15 text-emerald-600" : "bg-accent-soft text-accent",
              )}
              title={r.ok ? "Verified" : "Not resolving yet"}
            >
              {r.ok ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <X className="h-3 w-3" strokeWidth={2.5} />}
            </span>
            <span className="truncate">{r.value}</span>
          </span>
          <button
            onClick={() => {
              void navigator.clipboard?.writeText(r.value);
              setCopied(r.value);
              window.setTimeout(() => setCopied((c) => (c === r.value ? null : c)), 1400);
            }}
            aria-label="Copy value"
            className="grid h-7 w-7 place-items-center rounded-full text-subtle hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5"
          >
            {copied === r.value ? (
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
            ) : (
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
          </button>
        </div>
      ))}
    </div>
  );
}

function DomainRow({
  d,
  defaultOpen,
  projectId,
}: {
  d: DomainView;
  defaultOpen?: boolean;
  projectId?: string;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const qc = useQueryClient();
  const canVerify = !!projectId && !!d.id;
  const verifyMut = useMutation({
    mutationFn: () => builder.verifyDomain(projectId!, d.id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["domains", projectId] });
    },
  });
  const unresolved = d.dns.filter((r) => !r.ok).length;
  return (
    <div className="border-b border-hairline/60 px-5 py-4 last:border-0">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
          <Globe className="h-[18px] w-[18px]" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[15px] font-semibold tracking-tight text-ink-strong">
              {d.domain}
            </span>
            {d.primary && (
              <span className="inline-flex items-center gap-1 rounded-chip bg-accent-soft px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-accent">
                <Star className="h-3 w-3" strokeWidth={2} /> Primary
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <StatusPill tone={statusTone(d.status)}>{STATUS_LABEL[d.status]}</StatusPill>
            <SslBadge ssl={d.ssl} />
            {unresolved > 0 && (
              <span className="font-mono text-[11px] text-accent">
                {unresolved} record{unresolved > 1 ? "s" : ""} not resolving
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="inline-flex shrink-0 items-center gap-1 rounded-btn px-2.5 py-1.5 text-[13px] text-muted transition-colors hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5"
        >
          DNS
          <ChevronDown
            className={cn("h-4 w-4 transition-transform duration-200", open && "rotate-180")}
            strokeWidth={1.75}
          />
        </button>
      </div>
      {open && (
        <div className="animate-fade-up pl-12">
          <DnsTable dns={d.dns} />
          <div className="mt-2.5 flex flex-wrap items-center gap-3">
            {canVerify && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => verifyMut.mutate()}
                disabled={verifyMut.isPending}
              >
                {verifyMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                )}
                {verifyMut.isPending ? "Checking DNS…" : d.status === "live" ? "Re-check DNS" : "Verify DNS"}
              </Button>
            )}
          </div>
          {verifyMut.isError && (
            <p className="mt-2 text-[12px] text-accent">
              {errMessage(verifyMut.error, "Couldn't verify DNS. Check the records and try again.")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function DomainsSection() {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState("");
  const [newlyAdded, setNewlyAdded] = useState<string | null>(null);
  const ws = useWorkspace();
  const qc = useQueryClient();
  const projectId = ws.currentProjectId;
  const domainsQ = useDomains(projectId);

  const createMut = useMutation({
    mutationFn: (hostname: string) => builder.createDomain(projectId!, { hostname }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["domains", projectId] });
      setAdding(false);
      setValue("");
      setNewlyAdded(created.hostname);
    },
  });

  const realItems = domainsQ.data?.items;
  const isSample = !ws.live || !projectId || !!domainsQ.error || !realItems;
  const list: DomainView[] = isSample ? seedDomains : realItems!.map(mapBackendDomain);
  const firstPending = list.findIndex((d) => d.status !== "live");

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-4">
        <div>
          <p className="eyebrow mb-1 flex items-center gap-2">
            Custom domains {isSample && <SampleChip />}
          </p>
          <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
            Your pages, on your own domains
          </h3>
        </div>
        {!adding && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.75} /> Add domain
          </Button>
        )}
      </div>

      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const hostname = value.trim();
            if (!hostname || !projectId || createMut.isPending) return;
            createMut.mutate(hostname);
          }}
          className="animate-fade-up border-b border-hairline bg-paper-2/60 px-5 py-4 dark:bg-white/5"
        >
          <label className="eyebrow mb-1.5 block">Domain to connect</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-btn border border-hairline bg-paper px-3 focus-within:border-accent">
              <Globe className="h-4 w-4 shrink-0 text-subtle" strokeWidth={1.5} />
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="launch.yourbrand.com"
                className="h-11 w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-subtle"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="coral"
                size="md"
                disabled={!value.trim() || !projectId || createMut.isPending}
              >
                {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />}
                {createMut.isPending ? "Adding…" : "Add domain"}
              </Button>
              <Button
                type="button"
                variant="quiet"
                size="md"
                onClick={() => {
                  setAdding(false);
                  setValue("");
                  createMut.reset();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
          {createMut.isError ? (
            <p className="mt-2 text-[12px] text-accent">
              {errMessage(createMut.error, "Couldn't add this domain. Check the hostname and try again.")}
            </p>
          ) : (
            <p className="mt-2 text-[12px] text-subtle">
              We&apos;ll show the exact DNS records to add, then verify them — provisioning takes a
              few minutes.
            </p>
          )}
        </form>
      )}

      {domainsQ.isLoading && projectId ? (
        <div className="space-y-3 px-5 py-4">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-paper-2 dark:bg-white/5" />
              <div className="flex-1 space-y-1.5">
                <SkeletonLine className="h-3.5 w-44" />
                <SkeletonLine className="h-3 w-28" />
              </div>
            </div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <Globe className="mx-auto mb-2 h-6 w-6 text-subtle" strokeWidth={1.5} />
          <p className="text-[14px] text-muted">No custom domains yet</p>
          <p className="mt-0.5 text-[12px] text-subtle">
            Add one above to serve your pages on your own hostname.
          </p>
        </div>
      ) : (
        <div>
          {list.map((d, i) => (
            <DomainRow
              key={d.domain}
              d={d}
              projectId={isSample ? undefined : projectId}
              defaultOpen={d.domain === newlyAdded || (d.status !== "live" && i === firstPending)}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
